import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cva } from 'class-variance-authority';
import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react';
import {} from 'react';
import { cn } from '../lib/cn.js';
/**
 * An inline message about the thing next to it.
 *
 * Distinct from `ErrorState`, and the distinction is the one most often got
 * wrong. `ErrorState` is a whole region's substitute — it has a large icon and
 * its own heading because it replaces content that could not load. Using it for
 * a form failure gives you a page-sized graphic and a generic "Something went
 * wrong" sitting above a sentence that already said precisely what happened.
 *
 * This is for when the content is fine and something about it needs saying:
 * credentials that did not match, a record another user just changed, a
 * warning about what a save will do.
 *
 * The role follows the tone rather than being a prop. An error appearing after
 * a submit is information the user did not ask for and must be announced, so
 * it is `alert`. Anything else is `status`, which a screen reader queues
 * instead of interrupting.
 */
const alertVariants = cva('flex items-start gap-2.5 rounded-md border px-3 py-2.5 text-sm', {
    variants: {
        tone: {
            info: 'border-info/30 bg-info/10 text-foreground',
            success: 'border-success/30 bg-success/10 text-foreground',
            warning: 'border-warning/40 bg-warning/10 text-foreground',
            error: 'border-destructive/40 bg-destructive/10 text-foreground',
        },
    },
    defaultVariants: { tone: 'info' },
});
const TONE_ICON = {
    info: Info,
    success: CircleCheck,
    warning: TriangleAlert,
    error: CircleAlert,
};
const TONE_ICON_COLOUR = {
    info: 'text-info',
    success: 'text-success',
    warning: 'text-warning',
    error: 'text-destructive',
};
export function Alert({ tone = 'info', title, children, className, }) {
    const Icon = TONE_ICON[tone];
    return (_jsxs("div", { role: tone === 'error' ? 'alert' : 'status', className: cn(alertVariants({ tone }), className), children: [_jsx(Icon, { className: cn('mt-0.5 h-4 w-4 shrink-0', TONE_ICON_COLOUR[tone]), "aria-hidden": "true" }), _jsxs("div", { className: "min-w-0", children: [title === undefined ? null : _jsx("p", { className: "font-medium", children: title }), _jsx("div", { className: cn(title !== undefined && 'text-muted-foreground'), children: children })] })] }));
}
//# sourceMappingURL=alert.js.map